import type { TFunction } from 'i18next';
import type { Service, TimeSlot } from '@minibook/shared';
import { formatPrice, type PriceLocale } from '../../formatting.js';
import type { BookingPageCode, BookingStep } from '../bookingConstants.js';

type BookingSummaryBarProps = {
  t: TFunction;
  currentPage: BookingPageCode;
  step: BookingStep;
  priceLocale: PriceLocale;
  selectedServices: Service[];
  selectedProducts: string[];
  selectedSlot: TimeSlot | null;
  totalDuration: number;
  totalWithProducts: number;
  hasProducts: boolean;
  summaryButtonLabel: string;
  loading: boolean;
  submitting: boolean;
  rateLimitRetryAfter: number | null;
  formatSlotTime: (isoString: string | undefined | null) => string;
  onContinue: () => void;
  onGoToServices: () => void;
  onGoToProducts: () => void;
  onGoToDateTime: () => void;
};

export function BookingSummaryBar({
  t,
  currentPage,
  step,
  priceLocale,
  selectedServices,
  selectedProducts,
  selectedSlot,
  totalDuration,
  totalWithProducts,
  hasProducts,
  summaryButtonLabel,
  loading,
  submitting,
  rateLimitRetryAfter,
  formatSlotTime,
  onContinue,
  onGoToServices,
  onGoToProducts,
  onGoToDateTime,
}: BookingSummaryBarProps) {
  if (currentPage === 0) return null;

  const emptyDash = '—';
  const servicesPart = `${t('booking.servicesLabel')} ${selectedServices.length > 0 ? selectedServices.length : emptyDash}`;
  const productsPart = `${t('booking.productsLabel')} ${selectedProducts.length > 0 ? selectedProducts.length : emptyDash}`;
  const timeLineValue = (() => {
    if (!selectedSlot) return null;
    const start = formatSlotTime(selectedSlot.startAt);
    if (selectedServices.length > 0 && totalDuration > 0) {
      const endMs = new Date(selectedSlot.startAt).getTime() + totalDuration * 60 * 1000;
      const end = formatSlotTime(new Date(endMs).toISOString());
      return `${start}–${end}`;
    }
    return start;
  })();
  const timeLine = `${t('booking.timeLabel')} ${timeLineValue ?? emptyDash}`;

  const SummaryStatus = () => (
    <div className="booking-summary-status booking-mb-md">
      <div className="booking-flex-between" style={{ alignItems: 'center' }}>
        <div
          className="booking-text-sm booking-color-muted"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}
        >
          <span>{servicesPart}</span>
          <span style={{ color: 'var(--color-text-muted)' }}>•</span>
          <span>{productsPart}</span>
        </div>
        <div className="booking-text-sm booking-color-muted">{t('booking.sumLabel')}</div>
      </div>
      <div className="booking-flex-between" style={{ alignItems: 'center', marginTop: 'var(--spacing-xs)' }}>
        <div className="booking-text-sm booking-color-muted">{timeLine}</div>
        <div className="booking-text-lg booking-font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {formatPrice(totalWithProducts, priceLocale)} {t('booking.currency')}
        </div>
      </div>
    </div>
  );

  if (currentPage === 5 && selectedProducts.length > 0) {
    const canBook = selectedSlot != null;
    return (
      <div className="booking-summary-fixed">
        <SummaryStatus />
        <div className="booking-summary-two-buttons">
          <button type="button" onClick={onGoToServices} className="booking-button booking-button-primary">
            {t('booking.selectService')}
          </button>
          <button type="button" onClick={onGoToDateTime} className="booking-button booking-button-primary">
            {t('booking.selectDateTime')}
          </button>
        </div>
        {canBook && (
          <button
            type="button"
            onClick={onContinue}
            className="booking-button booking-button-primary booking-button-full"
            style={{ marginTop: 'var(--spacing-md)' }}
          >
            {t('booking.continue')}
          </button>
        )}
      </div>
    );
  }

  if (currentPage === 1 && step === 1) {
    if (hasProducts) {
      const canBook = selectedSlot != null;
      return (
        <div className="booking-summary-fixed">
          <SummaryStatus />
          <div className="booking-summary-two-buttons">
            <button type="button" onClick={onGoToProducts} className="booking-button booking-button-primary">
              {t('booking.selectProduct')}
            </button>
            <button type="button" onClick={onGoToDateTime} className="booking-button booking-button-primary">
              {t('booking.selectDateTime')}
            </button>
          </div>
          {canBook && (
            <button
              type="button"
              onClick={onContinue}
              className="booking-button booking-button-primary booking-button-full"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              {t('booking.continue')}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="booking-summary-fixed">
        <SummaryStatus />
        <button type="button" onClick={onContinue} className="booking-button booking-button-primary booking-button-full">
          {summaryButtonLabel}
        </button>
      </div>
    );
  }

  if (currentPage === 2 && step === 2) {
    if (hasProducts) {
      return (
        <div className="booking-summary-fixed">
          <SummaryStatus />
          <div className="booking-summary-two-buttons">
            <button type="button" onClick={onGoToProducts} className="booking-button booking-button-primary">
              {t('booking.selectProduct')}
            </button>
            <button type="button" onClick={onGoToServices} className="booking-button booking-button-primary">
              {t('booking.selectService')}
            </button>
          </div>
          {(selectedServices.length > 0 || selectedProducts.length > 0) && (
            <button
              type="button"
              onClick={onContinue}
              className="booking-button booking-button-primary booking-button-full"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              {t('booking.continue')}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="booking-summary-fixed">
        <SummaryStatus />
        <button type="button" onClick={onContinue} className="booking-button booking-button-primary booking-button-full">
          {summaryButtonLabel}
        </button>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="booking-summary-fixed">
        <SummaryStatus />
        <button
          type="submit"
          form="booking-client-form"
          disabled={loading || submitting || rateLimitRetryAfter !== null}
          className="booking-button booking-button-primary booking-button-full"
          style={{ marginTop: 'var(--spacing-md)' }}
        >
          {loading || submitting ? t('booking.creating') : t('booking.confirm')}
        </button>
      </div>
    );
  }

  return (
    <div className="booking-summary-fixed">
      <SummaryStatus />
      <button type="button" onClick={onContinue} className="booking-button booking-button-primary booking-button-full">
        {summaryButtonLabel}
      </button>
    </div>
  );
}
